import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { ConfirmModal } from '@/components/ConfirmModal';
import {
  ArrowLeft, Edit2, Phone, Mail, MapPin, User, Tag,
  Calendar, Activity, Loader2, Trash2, MessageCircle, Plus, Clock, Download,
  Info, Copy, Paperclip, Check, Megaphone
} from 'lucide-react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { LeadFormModal } from '@/components/leads/LeadFormModal';
import { FollowupDrawer } from '@/components/leads/FollowupDrawer';
import { ScoreBadge } from '@/components/leads/ScoreBadge';
import { ScoreBreakdownDrawer } from '@/components/leads/ScoreBreakdownDrawer';
import { DuplicateHistorySection } from '@/components/leads/DuplicateHistorySection';
import { LeadWhatsAppPanel } from '@/components/leads/LeadWhatsAppPanel';
import { LeadCallsPanel } from '@/components/leads/LeadCallsPanel';
import { MeetingsTab } from '@/components/leads/MeetingsTab';
import { LeadDocumentsTab } from '@/components/leads/LeadDocumentsTab';
import { LeadUtmTab } from '@/components/leads/LeadUtmTab';
import { toast } from '@/components/ui/toast';
import api from '@/lib/api';
import { formatDate, formatDateTime } from '@/lib/dateFormat';
import { formatLeadPhone } from '@/components/ui/PhoneInput';

const priorityColors: Record<string, string> = {
  high: 'bg-red-50 text-red-600 border border-red-200',
  medium: 'bg-amber-50 text-amber-600 border border-amber-200',
  low: 'bg-teal-50 text-teal-600 border border-teal-200',
};

const activityIcons: Record<string, string> = {
  lead_created: '🟢',
  lead_updated: '✏️',
  lead_status_changed: '🔄',
  ai_call: '📞',
  meeting_ai_summary: '✨',
};

// Icon + label for a workflow step (WhatsApp / Email / SMS / Call / Notify).
const STEP_META: Record<string, { icon: string; label: string }> = {
  SEND_WHATSAPP: { icon: '📱', label: 'WhatsApp' },
  SEND_EMAIL: { icon: '✉️', label: 'Email' },
  SEND_SMS: { icon: '💬', label: 'SMS' },
  MAKE_AI_CALL: { icon: '📞', label: 'AI Call' },
  NOTIFY: { icon: '🔔', label: 'Notification' },
};
const STATUS_CHIP: Record<string, string> = {
  SENT: 'bg-emerald-100 text-emerald-700',
  SKIPPED: 'bg-slate-100 text-slate-500',
  FAILED: 'bg-red-100 text-red-700',
  completed: 'bg-emerald-100 text-emerald-700',
  failed: 'bg-red-100 text-red-700',
  no_answer: 'bg-slate-100 text-slate-500',
  ringing: 'bg-amber-100 text-amber-700',
  in_progress: 'bg-blue-100 text-blue-700',
};

// One row in the unified Activity timeline — renders a system activity, a
// workflow delivery (with SENT/SKIPPED/FAILED + reason) or an AI call.
function ActivityRow({ item }: { item: any }) {
  if (item.kind === 'workflow') {
    const m = STEP_META[item.stepType] ?? { icon: '📌', label: item.stepType };
    const recip = item.recipientType ? String(item.recipientType).charAt(0) + String(item.recipientType).slice(1).toLowerCase() : '';
    return (
      <div className="flex gap-3">
        <div className="flex-shrink-0 mt-0.5 text-base leading-none">{m.icon}</div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-slate-700 flex items-center gap-2 flex-wrap">
            {m.label}{recip && <span className="text-slate-400">→ {recip}</span>}
            <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${STATUS_CHIP[item.status] ?? 'bg-slate-100 text-slate-600'}`}>{item.status}</span>
          </p>
          {(item.message || item.info) && <p className="text-xs text-muted-foreground break-words">{item.message || item.info}</p>}
          <p className="text-xs text-muted-foreground mt-0.5">{formatDateTime(item.at)}</p>
        </div>
      </div>
    );
  }
  if (item.kind === 'call') {
    return (
      <div className="flex gap-3">
        <div className="flex-shrink-0 mt-0.5 text-base leading-none">📞</div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-slate-700 flex items-center gap-2 flex-wrap">
            AI call
            <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold capitalize ${STATUS_CHIP[item.status] ?? 'bg-slate-100 text-slate-600'}`}>{String(item.status).replace('_', ' ')}</span>
            {item.outcome && <span className="text-slate-400 capitalize">{String(item.outcome).replace(/_/g, ' ')}</span>}
            {typeof item.durationSec === 'number' && <span className="text-slate-400">{item.durationSec}s</span>}
          </p>
          {item.summary && <p className="text-xs text-muted-foreground break-words">{item.summary}</p>}
          <p className="text-xs text-muted-foreground mt-0.5">{formatDateTime(item.at)}</p>
        </div>
      </div>
    );
  }
  // system activity
  return (
    <div className="flex gap-3">
      <div className="flex-shrink-0 mt-0.5 text-base leading-none">{activityIcons[item.actionType] || '📌'}</div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-slate-700 capitalize">{String(item.actionType).replace(/_/g, ' ')}</p>
        {item.actionType === 'lead_status_changed' && item.payload?.status && (
          <p className="text-xs text-muted-foreground">{item.payload.status.from} → {item.payload.status.to}</p>
        )}
        {item.actionType === 'ai_call' && (
          <p className="text-xs text-muted-foreground">
            {item.payload?.outcome ? <span className="capitalize font-medium">{String(item.payload.outcome).replace(/_/g, ' ')}</span> : 'completed'}
            {item.payload?.summary ? ` · ${item.payload.summary}` : ''}
          </p>
        )}
        {item.actionType === 'meeting_ai_summary' && item.payload?.summary && (
          <p className="text-xs text-muted-foreground whitespace-pre-line">{item.payload.summary}</p>
        )}
        <p className="text-xs text-muted-foreground mt-0.5">{item.actor ? `by ${item.actor} · ` : ''}{formatDateTime(item.at)}</p>
      </div>
    </div>
  );
}

// Compact label/value tile for the info strip under the header.
function InfoTile({ icon: Icon, label, children }: { icon: any; label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-3 min-w-0">
      <div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
        <Icon className="h-4 w-4 text-slate-500" />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-slate-500">{label}</p>
        <p className="text-sm font-semibold text-slate-800 truncate">{children}</p>
      </div>
    </div>
  );
}

// "19 h ago" style relative time.
function timeAgo(iso: string): string {
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
  const d = Math.floor(s / 86400);
  return d < 30 ? `${d} d ago` : formatDate(iso);
}

function InfoRow({ icon: Icon, label, value }: { icon: any; label: string; value?: string | null }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5 h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
        <Icon className="h-4 w-4 text-slate-500" />
      </div>
      <div>
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-sm font-medium text-slate-800">{value}</p>
      </div>
    </div>
  );
}

interface CustomFieldDef {
  id: string; label: string; fieldType: string;
  keyValues: any[]; isRequired: boolean; isActive: boolean; entityType: string;
}

// Meeting interactions are LeadActivity rows (not LeadFollowup). The Followups
// tab merges these in so a cancelled/rescheduled meeting or a meeting remark
// shows up alongside manual followups — they're all lead interactions.
const MEETING_ACTIVITY_TYPES = [
  'meeting_remark',
  'meeting_status_changed',
  'meeting_rescheduled',
  'meeting_booked_from_public',
  'meeting_booked_from_lead',
];

// Booking status → the lead status it sets (mirrors backend
// AvailabilityService.MEETING_LEAD_STATUS).
const MEETING_LEAD_STATUS: Record<string, string> = {
  CONFIRMED: 'Meeting Booked', COMPLETED: 'Meeting Completed', CANCELLED: 'Meeting Canceled',
  NO_SHOW: 'Meeting No-show', RESCHEDULED: 'Meeting Rescheduled',
};

const MEETING_STATUS_WORD: Record<string, string> = {
  CONFIRMED: 'confirmed', COMPLETED: 'completed', CANCELLED: 'cancelled',
  NO_SHOW: 'marked no-show', RESCHEDULED: 'rescheduled',
};

function fmt12(t?: string): string {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  if (Number.isNaN(h)) return t;
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
}

// One-line label for a meeting activity shown in the Followups timeline.
// Mirrors the Meetings-tab timeline wording.
function meetingActivityText(a: any): string {
  const p = a.payload ?? {};
  switch (a.actionType) {
    case 'meeting_remark':
      return p.remark || 'Meeting remark';
    case 'meeting_status_changed':
      return `Meeting ${MEETING_STATUS_WORD[p.to] ?? 'updated'}${p.reason ? ` — ${p.reason}` : ''}`;
    case 'meeting_rescheduled': {
      const from = p.from?.date ? `${formatDate(p.from.date)}${p.from?.startTime ? `, ${fmt12(p.from.startTime)}` : ''}` : '—';
      const to = p.to?.date ? `${formatDate(p.to.date)}${p.to?.startTime ? `, ${fmt12(p.to.startTime)}` : ''}` : '—';
      return `Meeting rescheduled: ${from} → ${to}`;
    }
    case 'meeting_booked_from_public':
      return 'Meeting booked via public calendar link';
    case 'meeting_booked_from_lead':
      return 'Meeting booked from lead detail';
    default:
      return String(a.actionType).replace(/_/g, ' ');
  }
}

export function LeadDetailPage() {
  const { id } = useParams<{ id: string }>();
  return (
    <DashboardLayout title="Lead Detail">
      {id && <LeadDetailView leadId={id} />}
    </DashboardLayout>
  );
}

/**
 * Lead detail content. Rendered by the full page (/leads/:id) and by the
 * Leads-list side drawer (`embedded`) — one component so both stay identical.
 * Embedded: tab lives in local state (not the URL) and back/delete call
 * onClose instead of navigating; onChanged lets the list refresh.
 */
export function LeadDetailView({ leadId, embedded = false, onClose, onChanged }: {
  leadId: string;
  embedded?: boolean;
  onClose?: () => void;
  onChanged?: () => void;
}) {
  const id = leadId;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [localTab, setLocalTab] = useState('overview');
  // Full page keeps the active tab in the URL (?tab=activity) so refresh /
  // back-button / deep-links land on the same tab.
  const activeTab = embedded ? localTab : (searchParams.get('tab') || 'overview');
  const setActiveTab = (tab: string) => {
    if (embedded) setLocalTab(tab);
    else setSearchParams((prev) => { prev.set('tab', tab); return prev; }, { replace: true });
  };
  const goBack = () => (onClose ? onClose() : navigate('/leads'));
  const { user: me } = useAuth();
  const isAdminOrManager = me?.role === 'admin' || me?.role === 'manager';
  const [lead, setLead] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showEdit, setShowEdit] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [customFieldDefs, setCustomFieldDefs] = useState<CustomFieldDef[]>([]);
  const [followups, setFollowups] = useState<any[]>([]);
  const [loadingFollowups, setLoadingFollowups] = useState(true);
  const [showFollowupDrawer, setShowFollowupDrawer] = useState(false);
  const [showScoreDrawer, setShowScoreDrawer] = useState(false);
  const [slaSeconds, setSlaSeconds] = useState(60);
  const [activityLog, setActivityLog] = useState<any[]>([]);

  useEffect(() => {
    // Tenant speed-to-lead target (default 60s) — drives the SLA badge threshold.
    api.get('/settings/speed-to-lead')
      .then(({ data }) => setSlaSeconds(Number(data?.slaSeconds) || 60))
      .catch(() => {});
  }, []);

  // Unified activity log (activities + workflow deliveries + calls) for the Activity tab.
  useEffect(() => {
    if (activeTab !== 'activity' || !lead?.id) return;
    api.get(`/leads/${lead.id}/activity-log`)
      .then(({ data }) => setActivityLog(Array.isArray(data) ? data : []))
      .catch(() => setActivityLog([]));
  }, [activeTab, lead?.id]);

  useEffect(() => {
    // Custom fields rarely change — cache in sessionStorage for the tab's life
    // so repeat Lead Detail visits skip this round-trip.
    const CACHE_KEY = 'custom-fields-lead';
    const cached = sessionStorage.getItem(CACHE_KEY);
    if (cached) {
      try {
        setCustomFieldDefs(JSON.parse(cached));
      } catch {}
    }
    api.get('/settings/custom-fields')
      .then(r => {
        const filtered = (Array.isArray(r.data) ? r.data : [])
          .filter((f: CustomFieldDef) => f.entityType === 'LEAD' && f.isActive);
        setCustomFieldDefs(filtered);
        // sessionStorage quota / disabled storage — silent OK, custom fields
        // will just re-fetch next visit.
        try { sessionStorage.setItem(CACHE_KEY, JSON.stringify(filtered)); } catch {}
      })
      .catch(err => console.warn('Failed to load custom fields:', err?.message));
  }, []);

  // /leads/:id now returns followups (with color) inline. One request instead
  // of two. Frontend still keeps followups in a separate state for simpler
  // re-rendering after the followup drawer saves.
  const fetchLead = async () => {
    setLoadingFollowups(true);
    try {
      const { data } = await api.get(`/leads/${id}`);
      setLead(data);
      setFollowups(Array.isArray(data?.followups) ? data.followups : []);
    } catch {
      goBack();
    } finally {
      setIsLoading(false);
      setLoadingFollowups(false);
    }
  };

  const fetchFollowups = async () => {
    setLoadingFollowups(true);
    try {
      const { data } = await api.get(`/leads/${id}/followups`);
      setFollowups(Array.isArray(data) ? data : []);
    } catch (err: any) {
      setFollowups([]);
      toast('error', err?.response?.data?.message || 'Failed to load followups');
    } finally {
      setLoadingFollowups(false);
    }
  };

  // Initial load uses the combined endpoint; fetchFollowups() is kept for
  // post-save refresh inside the followup drawer.
  useEffect(() => { fetchLead(); }, [id]);

  const handleDelete = () => setShowDeleteConfirm(true);

  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    try {
      await api.delete(`/leads/${id}`);
      onChanged?.();
      goBack();
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Failed to delete lead');
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-7 w-7 animate-spin text-primary" />
      </div>
    );
  }

  if (!lead) return null;

  const location = [lead.city, lead.state, lead.country].filter(Boolean).join(', ');

  // Meeting interactions (cancel/reschedule/remark/booked) live on the
  // activity log; merge them into the Followups timeline so they show as
  // lead interactions too. Sorted with real followups by createdAt DESC.
  const meetingActivities = ((lead.activities as any[]) ?? []).filter((a) =>
    MEETING_ACTIVITY_TYPES.includes(a.actionType),
  );
  const followupTimeline: Array<{ kind: 'followup' | 'meeting'; createdAt: string; data: any }> = [
    ...followups.map((f: any) => ({ kind: 'followup' as const, createdAt: f.createdAt, data: f })),
    ...meetingActivities.map((a: any) => ({ kind: 'meeting' as const, createdAt: a.createdAt, data: a })),
  ].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  // Header / info-strip derived values.
  const initials = String(lead.name || '?').trim().split(/\s+/).slice(0, 2).map((w: string) => w[0]?.toUpperCase() ?? '').join('') || '?';
  const primaryPhone = Array.isArray(lead.phones) && lead.phones.length > 0 ? lead.phones[0] : null;
  const primaryPhoneHref = primaryPhone ? `tel:${formatLeadPhone(primaryPhone).replace(/[^\d+]/g, '')}` : null;
  const primaryEmail: string | null = Array.isArray(lead.emails) && lead.emails.length > 0 ? lead.emails[0].email : null;
  const nextFollowupAt = followups
    .map((f: any) => f.nextFollowupAt)
    .filter((d: any) => d && new Date(d).getTime() >= Date.now())
    .sort((a: string, b: string) => new Date(a).getTime() - new Date(b).getTime())[0] ?? null;
  // Status trail = the last two status events in time order (previous ✓ →
  // latest): lead status edits, meeting actions (which move the lead status,
  // see availability MEETING_LEAD_STATUS) and follow-up statuses.
  const statusEvents: Array<{ name: string; at: number }> = [];
  for (const a of ((lead.activities as any[]) ?? [])) {
    const at = new Date(a.createdAt).getTime();
    if (a.actionType === 'lead_status_changed') {
      const ch = a.payload?.changes?.status ?? a.payload?.status;
      if (ch?.to) statusEvents.push({ name: String(ch.to), at });
    } else if (a.actionType === 'meeting_status_changed' && MEETING_LEAD_STATUS[a.payload?.to]) {
      statusEvents.push({ name: MEETING_LEAD_STATUS[a.payload.to], at });
    } else if (a.actionType === 'meeting_booked_from_public' || a.actionType === 'meeting_booked_from_lead') {
      statusEvents.push({ name: 'Meeting Booked', at });
    }
  }
  for (const f of followups) {
    if (f.status) statusEvents.push({ name: String(f.status), at: new Date(f.createdAt).getTime() });
  }
  statusEvents.sort((a, b) => a.at - b.at);
  // Collapse consecutive repeats (e.g. two "Call Completed" follow-ups).
  const statusHistory = statusEvents
    .map((e) => e.name)
    .filter((n, i, arr) => i === 0 || n.toLowerCase() !== arr[i - 1].toLowerCase());
  const statusTrail: string[] = statusHistory.length > 0
    ? statusHistory.slice(-2)
    : (lead.status ? [String(lead.status)] : []);
  // Filled custom fields shown inline in the info strip.
  const customFieldChips = customFieldDefs
    .map((field) => {
      const val = (lead.customFields as any)?.[field.id];
      if (val === undefined || val === null || val === '') return null;
      const value = field.fieldType === 'checkbox' ? (val ? 'Yes' : 'No')
        : Array.isArray(val) ? val.join(', ') : String(val);
      return { id: field.id, label: field.label, value };
    })
    .filter(Boolean) as Array<{ id: string; label: string; value: string }>;

  return (
    <>
      <div className={`${embedded ? '' : 'max-w-6xl'} space-y-4`}>
        {/* Header card — avatar, name, badges, quick actions, status trail */}
        <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-gradient-to-r from-white via-white to-indigo-50/70 p-4 sm:p-5 shadow-sm">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex items-start gap-3 min-w-0">
              {!embedded && (
                <Button variant="ghost" size="icon" onClick={goBack} className="-ml-2 mt-1 sm:ml-0 sm:mt-2 flex-shrink-0">
                  <ArrowLeft className="h-5 w-5" />
                </Button>
              )}
              <div className="flex h-11 w-11 sm:h-14 sm:w-14 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#6366F1] to-[#8B5CF6] text-base sm:text-lg font-bold text-white">
                {initials}
              </div>
              <div className="min-w-0">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <h1 className="text-xl sm:text-2xl font-bold text-slate-900 break-words">{lead.name}</h1>
                  {lead.leadNo && (
                    <span className="text-sm font-semibold text-slate-400">#{lead.leadNo}</span>
                  )}
                </div>
                <div className="mt-1.5 flex flex-wrap items-center gap-2">
                  {lead.priority && (
                    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${priorityColors[lead.priority?.toLowerCase()] || 'bg-slate-100 text-slate-500 border border-slate-200'}`}>
                      {lead.priority}
                    </span>
                  )}
                  {lead.source && (
                    <span className="text-xs text-slate-500">via <span className="font-semibold text-slate-700">{lead.source}</span></span>
                  )}
                  {lead.leadCategory && (
                    <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-indigo-50 text-indigo-600 border border-indigo-100">
                      {lead.leadCategory}
                    </span>
                  )}
                  <span className="text-xs text-slate-400">{formatDateTime(lead.createdAt)}</span>
                  {/* Score badge — clickable, opens breakdown drawer.
                      Score auto-updates when status/source/contacts change
                      via fire-and-forget recalc in LeadService. */}
                  <ScoreBadge
                    score={lead.score ?? 0}
                    size="sm"
                    onClick={() => setShowScoreDrawer(true)}
                  />
                </div>
                {/* Speed-to-lead SLA — responded-in time, or a missed-SLA flag. */}
                {(() => {
                  const a = lead.assignedAt ? new Date(lead.assignedAt).getTime() : null;
                  const f = lead.firstResponseAt ? new Date(lead.firstResponseAt).getTime() : null;
                  if (!a) return null;
                  const fmt = (s: number) => (s < 60 ? `${Math.round(s)} sec` : `${Math.round(s / 60)} min`);
                  if (f) {
                    return (
                      <span className="mt-2 inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700">
                        <Clock className="h-3 w-3" /> Responded in {fmt(Math.max(0, (f - a) / 1000))}
                      </span>
                    );
                  }
                  const elapsed = (Date.now() - a) / 1000;
                  const ok = elapsed <= slaSeconds;
                  return (
                    <span className={`mt-2 inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${ok ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-700'}`}>
                      <Clock className="h-3 w-3" /> {ok ? 'Awaiting first response' : 'SLA missed — respond ASAP'}
                    </span>
                  );
                })()}
              </div>
            </div>

            {/* Quick actions */}
            <div className="flex flex-wrap gap-2 lg:justify-end">
              {primaryPhoneHref && (
                <a href={primaryPhoneHref}
                  className="inline-flex h-10 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 sm:px-3.5 text-sm font-medium text-slate-700 hover:bg-slate-50">
                  <Phone className="h-4 w-4" /> <span className="hidden sm:inline">Call</span>
                </a>
              )}
              <button type="button" onClick={() => setActiveTab('whatsapp')}
                className="inline-flex h-10 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 sm:px-3.5 text-sm font-medium text-slate-700 hover:bg-slate-50">
                <MessageCircle className="h-4 w-4" /> <span className="hidden sm:inline">WhatsApp</span>
              </button>
              {primaryEmail && (
                <a href={`mailto:${primaryEmail}`}
                  className="inline-flex h-10 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 sm:px-3.5 text-sm font-medium text-slate-700 hover:bg-slate-50">
                  <Mail className="h-4 w-4" /> <span className="hidden sm:inline">Email</span>
                </a>
              )}
              <Button variant="outline" onClick={() => setShowEdit(true)} className="bg-white">
                <Edit2 className="h-4 w-4 sm:mr-2" /> <span className="hidden sm:inline">Edit</span>
              </Button>
              {/* GDPR Article 15 export — admin/manager only. Downloads a
                  JSON file with every stored data point for this lead
                  (activities, followups, emails, whatsapp, bookings). */}
              {isAdminOrManager && (
                <Button
                  variant="outline"
                  className="bg-white"
                  title="Export all stored data for this lead (GDPR)"
                  onClick={async () => {
                    try {
                      const { data } = await api.get(`/leads/${lead.id}/gdpr-export`);
                      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `lead-${lead.id}-${new Date().toISOString().slice(0, 10)}.json`;
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                      URL.revokeObjectURL(url);
                    } catch (err: any) {
                      toast('error', err?.response?.data?.message || 'Failed to export lead data');
                    }
                  }}
                >
                  <Download className="h-4 w-4" />
                </Button>
              )}
              {isAdminOrManager && (
                <Button variant="outline" onClick={handleDelete} disabled={isDeleting} className="bg-white text-red-600 hover:bg-red-50 border-red-200">
                  {isDeleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                </Button>
              )}
            </div>
          </div>

          {/* Status trail — previous status (done) → current status */}
          {statusTrail.length > 0 && (
            <div className="no-scrollbar mt-4 flex items-center overflow-x-auto">
              {statusTrail.map((st, i) => {
                const current = i === statusTrail.length - 1;
                return (
                  <span
                    key={`${st}-${i}`}
                    className={`inline-flex h-9 flex-shrink-0 whitespace-nowrap items-center gap-1.5 pl-5 pr-6 text-xs font-semibold capitalize ${i > 0 ? '-ml-2' : ''} ${
                      current ? 'bg-gradient-to-r from-[#6366F1] to-[#8B5CF6] text-white' : 'bg-emerald-50 text-emerald-700'
                    }`}
                    style={{
                      clipPath: i === 0
                        ? 'polygon(0 0, calc(100% - 12px) 0, 100% 50%, calc(100% - 12px) 100%, 0 100%)'
                        : 'polygon(0 0, calc(100% - 12px) 0, 100% 50%, calc(100% - 12px) 100%, 0 100%, 12px 50%)',
                    }}
                  >
                    {!current && <Check className="h-3.5 w-3.5" />}
                    {st}
                  </span>
                );
              })}
            </div>
          )}
        </div>

        {/* Key info strip */}
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2 lg:grid-cols-4">
            <InfoTile icon={Phone} label="Phone">
              {primaryPhone ? (
                <>
                  {formatLeadPhone(primaryPhone)}
                  {lead.phones.length > 1 && <span className="ml-1 text-xs font-normal text-slate-400">+{lead.phones.length - 1} more</span>}
                </>
              ) : '—'}
            </InfoTile>
            <InfoTile icon={Mail} label="Email">
              {primaryEmail ? (
                <a href={`mailto:${primaryEmail}`} className="text-[#6366F1] hover:underline break-all">{primaryEmail}</a>
              ) : '—'}
            </InfoTile>
            <InfoTile icon={MapPin} label="Location">{location || '—'}</InfoTile>
            <InfoTile icon={User} label="Assigned to">{lead.assignedTo?.name || 'Unassigned'}</InfoTile>
          </div>
          <div className="flex flex-col items-start gap-y-1.5 sm:flex-row sm:flex-wrap sm:items-center gap-x-6 border-t border-slate-100 px-4 py-3 text-sm">
            <span className="text-slate-500">Created: <span className="font-medium text-slate-800">{formatDateTime(lead.createdAt)}</span></span>
            <span className="text-slate-500">Last contact: <span className="font-medium text-slate-800">{lead.lastContactAt ? timeAgo(lead.lastContactAt) : '—'}</span></span>
            <span className="text-slate-500">Next follow-up: <span className="font-medium text-slate-800">{nextFollowupAt ? formatDateTime(nextFollowupAt) : '—'}</span></span>
            {customFieldChips.map((c) => (
              <span key={c.id} className="text-slate-500">{c.label}: <span className="font-medium text-slate-800">{c.value}</span></span>
            ))}
            <button
              type="button"
              onClick={() => setShowFollowupDrawer(true)}
              className="sm:ml-auto inline-flex items-center gap-1 text-sm font-medium text-[#6366F1] hover:underline"
            >
              <Plus className="h-3.5 w-3.5" /> Add follow-up
            </button>
          </div>
        </div>

        {/* Tabs + content in one card */}
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="no-scrollbar flex gap-1 overflow-x-auto border-b border-slate-100 px-2 sm:px-3 py-2.5">
            {[
              { key: 'overview', label: 'Overview', icon: Info },
              { key: 'followups', label: 'Follow-ups', icon: Clock, badge: followupTimeline.length },
              // Meetings tab — books/manages calendar bookings scoped to
              // THIS lead. Book Meeting drawer runs on the lead's assigned
              // counselor's calendar; remarks stored as LeadActivity.
              { key: 'meetings', label: 'Meetings', icon: Calendar },
              { key: 'activity', label: 'Activity', icon: Activity, badge: lead.activities?.length || 0 },
              { key: 'whatsapp', label: 'WhatsApp', icon: MessageCircle },
              { key: 'calls', label: 'Calls', icon: Phone },
              // UTM: first touch + every later (duplicate) arrival with its
              // campaign / ad data — replaces the Overview attribution cards.
              { key: 'utm', label: 'UTM', icon: Megaphone },
              { key: 'documents', label: 'Documents', icon: Paperclip },
              // Always visible; the count badge only shows once duplicates exist.
              { key: 'duplicates', label: 'Duplicates', icon: Copy, badge: lead.duplicateCount > 0 ? lead.duplicateCount : undefined },
            ].map((t) => {
              const Icon = t.icon;
              const active = activeTab === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => setActiveTab(t.key)}
                  className={`group flex flex-shrink-0 items-center gap-2 whitespace-nowrap rounded-xl px-3 sm:px-4 py-2 text-sm font-medium transition-colors focus:outline-none ${
                    active
                      ? 'bg-gradient-to-r from-[#6366F1] to-[#8B5CF6] text-white font-semibold shadow-sm'
                      : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                  }`}
                >
                  <Icon className={`h-4 w-4 transition-colors ${active ? 'text-white' : 'text-slate-400 group-hover:text-slate-600'}`} />
                  {t.label}
                  {typeof t.badge === 'number' && t.badge > 0 && (
                    <span className={`min-w-[18px] rounded-full px-1.5 text-center text-[11px] font-semibold leading-[18px] ${
                      active ? 'bg-white/25 text-white' : 'bg-slate-100 text-slate-600 group-hover:bg-slate-200'
                    }`}>{t.badge}</span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Tab content */}
          <div className="p-3 sm:p-5 [&_.bg-card]:!shadow-none">
            {/* Overview tab */}
            {activeTab === 'overview' && (
              <div className="space-y-4">
            {/* Contact Info */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Contact Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Phones come back as LeadPhone[] objects from backend.
                    Backend orders isPrimary first, then by createdAt. */}
                {Array.isArray(lead.phones) && lead.phones.length > 0 && (
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                      <Phone className="h-4 w-4 text-slate-500" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Phone</p>
                      {lead.phones.map((p: any, i: number) => (
                        <p key={p.id ?? i} className="text-sm font-medium text-slate-800">
                          {formatLeadPhone(p)}
                          {p.isPrimary && <span className="text-xs text-muted-foreground ml-1">primary</span>}
                          {p.isVerified && <span className="text-xs text-emerald-600 ml-1">✓ verified</span>}
                        </p>
                      ))}
                    </div>
                  </div>
                )}

                {Array.isArray(lead.emails) && lead.emails.length > 0 && (
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                      <Mail className="h-4 w-4 text-slate-500" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Email</p>
                      {lead.emails.map((e: any, i: number) => (
                        <p key={e.id ?? i} className="text-sm font-medium text-slate-800">
                          {e.email}
                          {e.isPrimary && <span className="text-xs text-muted-foreground ml-1">primary</span>}
                          {e.isVerified && <span className="text-xs text-emerald-600 ml-1">✓ verified</span>}
                        </p>
                      ))}
                    </div>
                  </div>
                )}

                {lead.address && (
                  <InfoRow icon={MapPin} label="Address" value={lead.address} />
                )}
                {location && (
                  <InfoRow icon={MapPin} label="City, State, Country" value={location} />
                )}
                <InfoRow icon={MapPin} label="Zipcode" value={lead.pincode} />
                <InfoRow icon={User} label="Assigned To" value={lead.assignedTo?.name} />
                <InfoRow icon={Calendar} label="Created" value={formatDateTime(lead.createdAt)} />

                {lead.hashtags?.length > 0 && (
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                      <Tag className="h-4 w-4 text-slate-500" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Hashtags</p>
                      <div className="flex flex-wrap gap-1.5">
                        {lead.hashtags.map((tag: string) => (
                          <span key={tag} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-50 text-indigo-600 border border-indigo-100">
                            #{tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Notes */}
            {lead.notes && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Notes</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{lead.notes}</p>
                </CardContent>
              </Card>
            )}

            {/* Custom Fields */}
            {customFieldDefs.length > 0 && lead.customFields && Object.keys(lead.customFields).length > 0 && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Custom Fields</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {customFieldDefs.map(field => {
                      const val = (lead.customFields as any)?.[field.id];
                      if (val === undefined || val === null || val === '') return null;
                      let display: string;
                      if (field.fieldType === 'checkbox') {
                        display = val ? 'Yes' : 'No';
                      } else if (field.fieldType === 'multi-select' && Array.isArray(val)) {
                        display = val.join(', ');
                      } else {
                        display = String(val);
                      }
                      return (
                        <div key={field.id}>
                          <p className="text-xs text-muted-foreground">{field.label}</p>
                          <p className="text-sm font-medium text-slate-800">{display}</p>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Followups tab */}
        {activeTab === 'followups' && (
          <div>
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <MessageCircle className="h-4 w-4" />
                    Followups
                  </CardTitle>
                  <button
                    onClick={() => setShowFollowupDrawer(true)}
                    className="flex items-center gap-1 text-xs font-medium text-[#6366F1] hover:underline"
                  >
                    <Plus className="h-3 w-3" /> Add Followup
                  </button>
                </div>
              </CardHeader>
              <CardContent>
                {loadingFollowups ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                  </div>
                ) : followupTimeline.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No followups yet</p>
                ) : (
                  <div className="space-y-3">
                    {followupTimeline.map((item) => item.kind === 'meeting' ? (
                      /* Meeting activity — read-only, managed from the Meetings tab */
                      <div key={`m-${item.data.id}`} className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
                        <div className="flex items-start justify-between gap-4">
                          <div className="min-w-0 flex-1">
                            <span className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-semibold text-indigo-600">
                              <Calendar className="h-3 w-3" /> Meeting
                            </span>
                            <p className="mt-2 text-sm text-slate-800">{meetingActivityText(item.data)}</p>
                            {item.data.actor?.name && (
                              <p className="mt-2 text-xs text-slate-400">by {item.data.actor.name}</p>
                            )}
                          </div>
                          <span className="whitespace-nowrap text-xs text-slate-400">{formatDateTime(item.createdAt)}</span>
                        </div>
                      </div>
                    ) : (() => { const f = item.data; return (
                      <div key={f.id} className="rounded-xl border border-slate-200 bg-white p-4 transition-colors hover:border-slate-300">
                        <div className="flex items-start justify-between gap-4">
                          {/* Left: status + remark + author */}
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-1.5">
                              <span
                                className="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold text-white"
                                style={{ backgroundColor: f.statusColor || '#6B7280' }}
                              >
                                {f.status}
                              </span>
                              {f.hashtags?.map((tag: string) => (
                                <span key={tag} className="rounded-full border border-indigo-100 bg-indigo-50 px-1.5 py-0.5 text-[10px] font-medium text-indigo-600">
                                  #{tag}
                                </span>
                              ))}
                            </div>
                            <p className="mt-2 text-sm text-slate-800">{f.remark}</p>
                            {f.user?.name && (
                              <p className="mt-2 text-xs text-slate-400">by {f.user.name}</p>
                            )}
                          </div>
                          {/* Right: created time + next-followup pill */}
                          <div className="flex flex-shrink-0 flex-col items-end gap-2">
                            <span className="whitespace-nowrap text-xs text-slate-400">{formatDateTime(f.createdAt)}</span>
                            {f.nextFollowupAt && (
                              <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700">
                                <Clock className="h-3 w-3" /> Next: {formatDateTime(f.nextFollowupAt)}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ); })())}
                  </div>
                )}
              </CardContent>
            </Card>

          </div>
        )}

        {/* Meetings tab — book / list / remark / status / reschedule
            calendar bookings scoped to this lead. Backend endpoints under
            /leads/:id/meetings (see availability.controller). */}
        {activeTab === 'meetings' && lead?.id && (
          <div>
            <MeetingsTab
              leadId={lead.id}
              hasAssignedCounselor={!!lead.assignedToId}
            />
          </div>
        )}

        {/* WhatsApp tab */}
        {activeTab === 'whatsapp' && lead?.id && (
          <div>
            <LeadWhatsAppPanel leadId={lead.id} />
          </div>
        )}

        {/* Calls tab (AI voice) — full width so the composer + history
            cards fill the tab strip's line, no left-inset gap. */}
        {activeTab === 'calls' && lead?.id && (
          <div>
            <LeadCallsPanel leadId={lead.id} consentGiven={!!lead.consentGiven} />
          </div>
        )}

        {/* Documents tab — attach bills / PDFs / images / spreadsheets etc.
            Files stored in Supabase Storage; see /leads/:id/documents. */}
        {activeTab === 'utm' && lead?.id && (
          <div>
            <LeadUtmTab lead={lead} />
          </div>
        )}

        {activeTab === 'documents' && lead?.id && (
          <div>
            <LeadDocumentsTab leadId={lead.id} />
          </div>
        )}

        {/* Activity tab */}
        {activeTab === 'activity' && (
          <div>
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Activity className="h-4 w-4" />
                  Activity
                </CardTitle>
              </CardHeader>
              <CardContent>
                {activityLog.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No activity yet</p>
                ) : (
                  <div className="space-y-4">
                    {activityLog.map((item: any, idx: number) => (
                      <ActivityRow key={idx} item={item} />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

            {/* Duplicates tab */}
            {activeTab === 'duplicates' && (
              <DuplicateHistorySection
                leadId={lead.id}
                duplicateCount={lead.duplicateCount}
              />
            )}
          </div>
        </div>
      </div>

      {showEdit && (
        <LeadFormModal
          lead={lead}
          onClose={() => setShowEdit(false)}
          onSaved={() => { setShowEdit(false); fetchLead(); onChanged?.(); }}
        />
      )}
      {showFollowupDrawer && (
        <FollowupDrawer
          lead={lead}
          onClose={() => setShowFollowupDrawer(false)}
          onSaved={() => { setShowFollowupDrawer(false); fetchLead(); onChanged?.(); }}
        />
      )}
      <ScoreBreakdownDrawer
        leadId={lead.id}
        leadName={lead.name}
        open={showScoreDrawer}
        onClose={() => setShowScoreDrawer(false)}
        onScoreUpdated={(newScore) => setLead((l: any) => l ? { ...l, score: newScore } : l)}
      />
      <ConfirmModal
        isOpen={showDeleteConfirm}
        title="Delete Lead?"
        message="Are you sure you want to delete this lead? This action cannot be undone."
        confirmText="Delete"
        onConfirm={handleConfirmDelete}
        onCancel={() => setShowDeleteConfirm(false)}
        isLoading={isDeleting}
      />
    </>
  );
}
